import { useAtomValue } from 'jotai'
import React from 'react'
import * as R from 'remeda'

import {
  type CustomerAttribute,
  type CustomerAttributeGroup,
} from '~/hooks/useSurveyQuery'
import { findByPath, getByPath, pathOr } from '~/utils'

import { sortFunctionAtom, tabulationResultAtom } from '../atoms'
import { countColumnKeyToId, type CountData } from '../utils'

import { useMapCountColumnKey } from './useMapCountColumnKey'
import { useMapDiffColumnKey } from './useMapDiffColumnKey'
import { useTabulatedCustomerAttributeGroups } from './useTabulatedCustomerAttributeGroups'

type CustomerAttributeData = CountData & {
  customerAttributeGroup: CustomerAttributeGroup
  customerAttribute: CustomerAttribute
}

export const useCustomerAttributesArrayData = (): CustomerAttributeData[][] => {
  const customerAttributeGroups = useTabulatedCustomerAttributeGroups()
  const mapCountColumnKey = useMapCountColumnKey()
  const mapDiffColumnKey = useMapDiffColumnKey()
  const tabulationResult = useAtomValue(tabulationResultAtom)
  const sort = useAtomValue(sortFunctionAtom)

  return React.useMemo(
    () =>
      customerAttributeGroups.map((customerAttributeGroup) =>
        R.pipe(
          customerAttributeGroup.customerAttributes,
          R.map((customerAttribute) => {
            const results = mapCountColumnKey((countColumnKey) =>
              R.pipe(
                tabulationResult ?? [],
                findByPath(
                  'byCustomerAttributeId',
                  countColumnKeyToId(countColumnKey),
                ),
                getByPath('result'),
              ),
            )

            const counts = mapCountColumnKey((countColumnKey) =>
              R.pipe(
                results[countColumnKey],
                pathOr('customerAttributes', []),
                findByPath('customerAttributeId', customerAttribute.id),
                pathOr('count', NaN),
              ),
            )
            const diffCounts = mapDiffColumnKey(
              (_diffColumnKey, [left, right]) => counts[left] - counts[right],
            )

            const ratios = mapCountColumnKey((countColumnKey) => {
              const count = counts[countColumnKey]
              const totalCount = results[countColumnKey]?.totalCount ?? NaN

              return count / totalCount
            })
            const diffRatios = mapDiffColumnKey(
              (_diffColumnKey, [left, right]) => ratios[left] - ratios[right],
            )

            return {
              customerAttributeGroup,
              customerAttribute,
              counts: {
                ...counts,
                ...diffCounts,
              },
              ratios: {
                ...ratios,
                ...diffRatios,
              },
            }
          }),
          sort,
        ),
      ),
    [
      customerAttributeGroups,
      mapCountColumnKey,
      mapDiffColumnKey,
      sort,
      tabulationResult,
    ],
  )
}
